import { ReactSVG } from 'react-svg';
import { IconSetNames } from './osc-icon-map';
import {
  EYIconSetNames,
  ESIconSetNames,
  HPIconSetNames,
  OLIconSetNames,
} from './osc-brand-icon-map';
import type {
  ESIconNames,
  EYIconNames,
  HPIconNames,
  OLIconNames,
  SupportedBrand,
} from './osc-brand-icon-map';
import type { IconVariant, IconNames } from './osc-icon-map';
import { ElementType } from 'react';

export const sizeToRemValue = {
  xs: '1.125',
  sm: '1.25',
  md: '1.75',
  lg: '2',
  xl: '2.5',
  '2xl': '3',
  '3xl': '3.75',
  '4xl': '4.5',
  '5xl': '6',
} as const;

const basePath = `/modules/assets/svgs/icons`;
export const baseIconPath = basePath;
export const brandIconPath = `${basePath}/brand`;

type BaseIconProps = Pick<React.ComponentProps<typeof ReactSVG>, 'title' | 'wrapper'> & {
  /**
   * For passing additional classes to the parent `svg` element
   */
  className?: string;
  /**
   * fallback component to display while SVG is loading
   */
  loadingComponent?: ElementType;
  /**
   * One of five size options; defaults to medium
   */
  size?: keyof typeof sizeToRemValue;
};

type IconPropsWithBrand<
  BrandCode extends SupportedBrand,
  BrandIconNames extends ReadonlyArray<string>,
> = BaseIconProps & {
  /**
   * Brand code for the icon
   */
  brandCode: BrandCode;
  /**
   * Name of the icon to display from a brand specific set
   */
  name: BrandIconNames[number];
  variant?: never;
};

export type OSCIconNames = (typeof IconNames)[number];

type IconPropsWithVariant = BaseIconProps & {
  brandCode?: never;
  /**
   * Name of the icon to display from the approved set
   */
  name: OSCIconNames;
  /**
   * Controls icon style: regular or solid; defaults to “regular”
   */
  variant?: IconVariant;
};

export type IconProps =
  | IconPropsWithVariant
  | IconPropsWithBrand<'ES', typeof ESIconNames>
  | IconPropsWithBrand<'EY', typeof EYIconNames>
  | IconPropsWithBrand<'HP', typeof HPIconNames>
  | IconPropsWithBrand<'OL', typeof OLIconNames>;

export function Icon({
  className,
  brandCode,
  loadingComponent,
  name,
  size = 'md',
  title,
  variant = 'regular',
  wrapper = 'span',
}: IconProps) {
  if (brandCode === 'ES' && !ESIconSetNames.has(name)) {
    return null;
  }
  if (brandCode === 'EY' && !EYIconSetNames.has(name)) {
    return null;
  }
  if (brandCode === 'HP' && !HPIconSetNames.has(name)) {
    return null;
  }

  if (brandCode === 'OL' && !OLIconSetNames.has(name)) {
    return null;
  }
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
  if (!brandCode && !IconSetNames.has(name as OSCIconNames)) {
    return null;
  }

  const sizeInRem = sizeToRemValue[size];
  const src = brandCode
    ? `${brandIconPath}/${brandCode}/${name}.svg`
    : `${baseIconPath}/${variant}/${name}.svg`;

  return (
    <ReactSVG
      beforeInjection={(svg) => {
        !title && svg.setAttribute('aria-hidden', 'true');
        title && svg.setAttribute('aria-label', title);
        svg.setAttribute('fill', 'currentColor');
        svg.setAttribute('width', `${sizeInRem}rem`);
        svg.setAttribute('height', `${sizeInRem}rem`);
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('data-testid', `icon-${name}`);
        className && svg.classList.add(...className.split(' '));
      }}
      afterInjection={(svg) => {
        if (svg) {
          const wrapperDiv = svg.parentElement?.parentElement;
          if (wrapperDiv) {
            wrapperDiv.removeAttribute('style');
          }
        }
      }}
      src={src}
      {...(title ? { title } : null)}
      style={{ height: `${sizeInRem}rem`, width: `${sizeInRem}rem` }}
      wrapper={wrapper}
      className="pointer-events-none block"
      loading={loadingComponent}
    />
  );
}

export default Icon;
